import { Activity, ActivityProgress, UserStats, Streak } from '@/types';
import { db } from './database';
import { calculateStreak, canCompleteToday, calculateLevel } from './streaks';

export class ActivityManager {
  async createActivity(activityData: Omit<Activity, 'id' | 'createdAt'>): Promise<Activity> {
    const activity: Activity = {
      ...activityData,
      id: crypto.randomUUID(),
      createdAt: new Date(),
    };

    await db.addActivity(activity);
    return activity;
  }

  async getActivities(): Promise<Activity[]> {
    return await db.getActivities();
  }

  async getActiveActivities(): Promise<Activity[]> {
    const activities = await db.getActivities();
    return activities.filter(activity => activity.isActive);
  }

  async updateActivity(activity: Activity): Promise<void> {
    await db.updateActivity(activity);
  }

  async deleteActivity(id: string): Promise<void> {
    await db.deleteActivity(id);
  }

  async completeActivity(activityId: string, note?: string): Promise<void> {
    const activity = await this.getActivityById(activityId);
    if (!activity) throw new Error('Activity not found');

    const existingProgress = await db.getProgress(activityId);
    const streak = calculateStreak(existingProgress);

    if (!canCompleteToday(streak.lastCompletedDate)) {
      throw new Error('Activity already completed today');
    }

    const progress: ActivityProgress = {
      id: crypto.randomUUID(),
      activityId,
      completedAt: new Date(),
      points: activity.pointsPerCompletion,
      note
    };

    await db.addProgress(progress);

    const updatedProgress = [...existingProgress, progress];
    const updatedStreak = calculateStreak(updatedProgress);
    await db.updateStreak(updatedStreak);

    await this.updateUserStats(activity.pointsPerCompletion);
  }

  async getActivityById(id: string): Promise<Activity | undefined> {
    const activities = await db.getActivities();
    return activities.find(activity => activity.id === id);
  }

  async getActivityProgress(activityId: string): Promise<ActivityProgress[]> {
    return await db.getProgress(activityId);
  }

  async getActivityStreak(activityId: string): Promise<Streak> {
    const progress = await db.getProgress(activityId);
    return calculateStreak(progress);
  }

  async getAllStreaks(): Promise<Streak[]> {
    const activities = await db.getActivities();
    const streaks: Streak[] = [];

    for (const activity of activities) {
      const progress = await db.getProgress(activity.id);
      const streak = calculateStreak(progress);
      streaks.push(streak);
    }

    return streaks;
  }

  async getUserStats(): Promise<UserStats> {
    return await db.getUserStats();
  }

  private async updateUserStats(pointsEarned: number): Promise<void> {
    const stats = await db.getUserStats();
    const allProgress = await db.getProgress();
    const allStreaks = await this.getAllStreaks();

    const newStats: UserStats = {
      ...stats,
      totalPoints: stats.totalPoints + pointsEarned,
      activitiesCompleted: allProgress.length,
      longestStreak: Math.max(...allStreaks.map(s => s.longest), 0),
      currentStreak: Math.max(...allStreaks.map(s => s.current), 0),
      level: calculateLevel(stats.totalPoints + pointsEarned)
    };

    await db.updateUserStats(newStats);
  }

  async getCompletedActivitiesToday(): Promise<string[]> {
    const today = new Date().toDateString();
    const allProgress = await db.getProgress();
    
    return allProgress
      .filter(progress => new Date(progress.completedAt).toDateString() === today)
      .map(progress => progress.activityId);
  }
}

export const activityManager = new ActivityManager();